import React from 'react';
import {ChipItem} from './ChipInputTypes';
import {Size, States} from '../types';
import {InputChip} from '../Chips';
import {ErrorMessage} from '../InputFields';
import {
  COLORS,
  ComponentMStyling,
  ComponentSStyling,
  ComponentTextStyle,
  focusStyles,
  scrollBarStyling
} from '../styles';
import styled, { useTheme } from 'styled-components';
import {LoadingIndicator} from '../LoadingIndicator';
import {useFocusVisibleRef} from '../common';
import {IconButton} from '../Button';
import {SystemIcons} from '../icons';

const Wrapper = styled.div`
  position: relative;
`;

const Loading = styled.div`
  height: 20px;
  width: 20px;
`;

export const ChipInputBoundItems = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 40px;
`;

export const ChipInputContentContainer = styled.div`
  width: 100%;
  background: transparent;
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 8px;
  margin-right: 6px;
  padding-right: 10px;
  padding-left: 16px;
  flex-wrap: nowrap;
  overflow-x: scroll;
  min-height: 48px;

  ${scrollBarStyling(Size.Small)}
  ::-webkit-scrollbar:horizontal {
    display: none;
  }

  .action-right:focus,
  button:focus {
    outline-offset: -4px !important;
  }
`;

export const ChipInputEl = styled.input`
  border: none;
  outline: none;

  height: 40px;
  min-width: 40px;
  display: inline-flex;
  flex: 1;
  background: transparent;

  ${ComponentMStyling(ComponentTextStyle.Regular, null)}
  color: ${props => COLORS.getColor('black', props.theme)} !important;

  &::placeholder {
    ${props => ComponentMStyling(ComponentTextStyle.Italic, COLORS.generateToken({componentType:'text',defaultVariant:'subtle'}, props.theme))}
  }
`;

export const ChipInputContainer = styled.div`
  width: 100%;
  height: auto;
  border-radius: 4px;
  margin-bottom: 4px;
  box-shadow: inset 0 0 0 1px ${props => COLORS.getColor('neutral_400', props.theme)};
  background-color: ${props => COLORS.generateToken({componentType:'bg-surface', defaultVariant:'default'}, props.theme)};
  box-sizing: border-box;
  cursor: text;
  outline: none;
  display: flex;
  align-items: center;

  ${props => ComponentMStyling(ComponentTextStyle.Regular, COLORS.generateToken({componentType:'text', defaultVariant:'default'}, props.theme))}
  &.small {
    padding: 0;
    min-height: 48px;

    ${ChipInputEl}::placeholder {
      ${props => ComponentSStyling(ComponentTextStyle.Italic, COLORS.generateToken({componentType:'text',defaultVariant:'subtle'}, props.theme))}
    }
  }

  &.medium {
    padding: 4px 0;
    min-height: 56px;

    ${ChipInputEl}::placeholder {
      ${props => ComponentMStyling(ComponentTextStyle.Italic, COLORS.generateToken({componentType:'text',defaultVariant:'subtle'}, props.theme))}
    }
  }

  &.multiline.medium {
    ${ChipInputContentContainer} {
      max-height: calc(48px * 3);
      flex-wrap: wrap;
      overflow-y: auto;
      overflow-x: unset;
    }
  }

  &.focus-visible-within,
  &:focus {
    ${focusStyles}
  }

  &:hover {
    box-shadow: inset 0 0 0 2px ${props => COLORS.getColor('primary_700', props.theme)};
  }

  &:focus-within {
    box-shadow: inset 0 0 0 2px ${props => COLORS.getColor('primary_800', props.theme)};
  }

  &.invalid,
  &.invalid:hover,
  &.invalid:focus {
    box-shadow: inset 0 0 0 2px ${props => COLORS.getColor('critical_500', props.theme)};
  }

  &.disabled {
    ${ChipInputContentContainer} {
      pointer-events: none;
    }

    cursor: not-allowed;
    box-shadow: inset 0 0 0 1px ${props => COLORS.getColor('neutral_100', props.theme)};
    outline: none;
    color: ${props => COLORS.getColor('neutral_300', props.theme)} !important;
  }
`;

export interface ChipInputFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>,  'type' | 'id' | 'required' | 'tabIndex' | 'value' | 'onChange' | 'onKeyDown' | 'placeholder' | 'onClick' | 'disabled' | 'size'> {
  inputId: string;
  items: ChipItem[];
  disabled?: boolean;
  placeholder?: string;
  altPlaceholder?: string;
  multiLine?: boolean;
  size?: Size.Large | Size.Medium | Size.Small;
  loading?: boolean;
  required?: boolean;

  validationMessage?: string;

  onRemoveItem: (item: ChipItem, index: number) => void;
  inputValue: string;
  onInputValueChange: (value: string) => void;
  onInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onActivationChange?: (active: boolean) => void;
}

export const ChipInputField = React.forwardRef<HTMLInputElement, ChipInputFieldProps>(
  (
    {
      inputId,
      items,
      disabled = false,
      placeholder,
      altPlaceholder,
      multiLine = true,
      size = Size.Medium,
      validationMessage,
      loading,
      onRemoveItem,
      onInputValueChange,
      onInputKeyDown,
      onActivationChange,
      inputValue,
      required,
      onBlur,
      ...rest
    },
    ref,
  ) => {
    const theme = useTheme();
    const containerRef = React.useRef<HTMLDivElement>(null);
    const inputRef = useFocusVisibleRef([containerRef]);
    
    React.useImperativeHandle(ref, () => inputRef.current, [inputRef])

    /**
     * Scroll container to last item on change
     * */
    React.useEffect(() => {
      if (inputRef.current && document.activeElement === inputRef.current) {
        inputRef.current.scrollIntoView({
          block: 'nearest',
          inline: 'nearest',
        });
      }
    }, [containerRef, inputRef, items]);

    /**
     * Remove last cheap on backspace
     * */
    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !inputRef.current?.value && !e.repeat && items.length > 0) {
        onRemoveItem(items[items.length - 1], items.length - 1);
      } else {
        onInputKeyDown(e);
      }
    };

    const handleItemRemove = (event: any, item: ChipItem, index: number) => {
      onRemoveItem(item, index);
      inputRef.current?.focus({focusVisible: event.detail !== 1} as any);
    };

    const handleInputClear = (e: any) => {
      onInputValueChange('');
      inputRef.current?.focus({focusVisible: e?.detail !== 1} as any);
    };

    const handleContainerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!containerRef.current || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;

      const items = containerRef.current.querySelectorAll('[tabindex="0"]') as NodeListOf<HTMLElement>;
      const currentIndex = Array.from(items).indexOf(e.target as any);
      const direction = e.key === 'ArrowLeft' ? -1 : 1;
      const item = items[currentIndex + direction];
      item?.focus({focusVisible: true} as any);

      onActivationChange && onActivationChange(false);
    };

    const cls = `${multiLine ? 'multiline' : ''} ${validationMessage ? 'invalid' : ''} ${size} ${loading ? 'loading' : ''} ${disabled ? 'disabled' : ''}`;

    return (
      <Wrapper onBlur={onBlur}>
        <ChipInputContainer
          data-testid="chip-input-container"
          ref={containerRef}
          onKeyDown={handleContainerKeyDown}
          onClick={() => !disabled && inputRef.current?.focus()}
          className={cls}>
          <ChipInputContentContainer>
            {items?.map((chip: ChipItem, index: number) => (
              <InputChip
                key={`chip_${index}`}
                icon={chip?.icon}
                size={size}
                disabled={chip.disabled || disabled}
                variant={chip.variant || States.Default}
                text={chip.label}
                onRemove={(e) => handleItemRemove(e, chip, index)}
              />
            ))}
            <ChipInputBoundItems>
              <ChipInputEl
                ref={inputRef}
                type="text"
                id={inputId}
                required={required}
                tabIndex={disabled ? -1 : 0}
                value={inputValue}
                onChange={(e) => onInputValueChange(e.target.value)}
                onKeyDown={handleInputKeyDown}
                placeholder={items?.length === 0 ? placeholder : altPlaceholder || ''}
                onClick={(e) => {
                  e.stopPropagation();
                  onActivationChange && onActivationChange(true);
                }}
                {...rest}
              />
              {inputValue && !disabled && (
                <IconButton
                  id={`clear-btn-${inputId}`}
                  dataTestId={`clear-btn-${inputId}`}
                  action={handleInputClear}
                  ref={(ref) => {
                    ref?.scrollIntoView({
                      block: 'nearest',
                      inline: 'nearest',
                    });
                  }}
                  useTransparentBackground={true}
                  shape={'circular'}
                  style={{marginLeft: '-8px'}}
                  focusOnClick={true}
                  variant={'secondary'}>
                  <SystemIcons.Clear/>
                </IconButton>
              )}

              {loading && (
                <Loading className={size}>
                  <LoadingIndicator size={Size.Small} color={COLORS.generateToken({componentType:'icon',defaultVariant:'subtle'}, theme)}/>
                </Loading>
              )}
            </ChipInputBoundItems>
          </ChipInputContentContainer>
        </ChipInputContainer>
        {validationMessage && (
          <ErrorMessage>
            <SystemIcons.TechnicalWarning color={COLORS.getColor('critical_400', theme)}/>
            <span>{validationMessage}</span>
          </ErrorMessage>
        )}
      </Wrapper>
    );
  },
);
